/**
 * Client Environment, Device, OS and Browser Detection Utility
 * Lightweight, robust, and zero-dependency parser for telemetry and debugging.
 */

export interface ClientEnvironmentSnapshot {
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  deviceModel: string;
  screenResolution: string;
  viewportSize: string;
  pixelRatio: number;
  isStandalonePWA: boolean;
  isOnline: boolean;
  connectionType?: string;
  connectionRtt?: number;
  language: string;
  userAgent: string;
}

export function parseUserAgent(uaString?: string): {
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  deviceModel: string;
} {
  const ua = uaString || (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  
  let os = 'Unknown OS';
  let osVersion = '';
  let browser = 'Unknown Browser';
  let browserVersion = '';
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  let deviceModel = 'PC / Mac';

  // 1. Detect OS & Version
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const isIPad = /iPad/i.test(ua) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    os = isIPad ? 'iPadOS' : 'iOS';
    deviceType = isIPad ? 'tablet' : 'mobile';
    deviceModel = isIPad ? 'Apple iPad' : 'Apple iPhone';

    const match = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?)/i);
    if (match) {
      osVersion = match[1].replace(/_/g, '.');
    }
  } else if (/Android/i.test(ua)) {
    os = 'Android';
    deviceType = /Mobile/i.test(ua) ? 'mobile' : 'tablet';
    deviceModel = deviceType === 'mobile' ? 'Android Phone' : 'Android Tablet';

    const match = ua.match(/Android\s+([\d.]+)/i);
    if (match) {
      osVersion = match[1];
    }
  } else if (/Windows NT/i.test(ua)) {
    os = 'Windows';
    deviceType = 'desktop';
    deviceModel = 'Desktop PC';

    if (/Windows NT 10.0/i.test(ua)) osVersion = '10 / 11';
    else if (/Windows NT 6.3/i.test(ua)) osVersion = '8.1';
    else if (/Windows NT 6.2/i.test(ua)) osVersion = '8';
    else if (/Windows NT 6.1/i.test(ua)) osVersion = '7';
  } else if (/Macintosh|Mac OS X/i.test(ua)) {
    // Check for iPad in desktop mode (Safari on iPadOS requests desktop site by default)
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
      os = 'iPadOS';
      deviceType = 'tablet';
      deviceModel = 'Apple iPad';
    } else {
      os = 'macOS';
      deviceType = 'desktop';
      deviceModel = 'Apple Mac';
      const match = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/i);
      if (match) {
        osVersion = match[1].replace(/_/g, '.');
      }
    }
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
    deviceType = 'desktop';
    deviceModel = 'Linux Desktop';
  }

  // 2. Detect Browser & Version
  if (/YaBrowser\/([\d.]+)/i.test(ua)) {
    browser = 'Yandex Browser';
    browserVersion = ua.match(/YaBrowser\/([\d.]+)/i)?.[1] || '';
  } else if (/SamsungBrowser\/([\d.]+)/i.test(ua)) {
    browser = 'Samsung Internet';
    browserVersion = ua.match(/SamsungBrowser\/([\d.]+)/i)?.[1] || '';
  } else if (/Edg\/([\d.]+)|Edge\/([\d.]+)/i.test(ua)) {
    browser = 'Microsoft Edge';
    browserVersion = ua.match(/(?:Edg|Edge)\/([\d.]+)/i)?.[1] || '';
  } else if (/OPR\/([\d.]+)|Opera\/([\d.]+)/i.test(ua)) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:OPR|Opera)\/([\d.]+)/i)?.[1] || '';
  } else if (/Chrome\/([\d.]+)|CriOS\/([\d.]+)/i.test(ua)) {
    const isCriOS = /CriOS/i.test(ua);
    browser = deviceType === 'desktop' ? 'Google Chrome' : (isCriOS ? 'Chrome iOS' : 'Chrome Mobile');
    browserVersion = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/i)?.[1] || '';
  } else if (/Firefox\/([\d.]+)|FxiOS\/([\d.]+)/i.test(ua)) {
    const isFxiOS = /FxiOS/i.test(ua);
    browser = isFxiOS ? 'Firefox iOS' : 'Mozilla Firefox';
    browserVersion = ua.match(/(?:Firefox|FxiOS)\/([\d.]+)/i)?.[1] || '';
  } else if (/Safari/i.test(ua) && /Version\/([\d.]+)/i.test(ua)) {
    browser = deviceType === 'desktop' ? 'Apple Safari' : 'Mobile Safari';
    browserVersion = ua.match(/Version\/([\d.]+)/i)?.[1] || '';
  } else if (os === 'iOS' || os === 'iPadOS') {
    browser = 'Mobile Safari / WebKit Webview';
  }

  return {
    os,
    osVersion,
    browser,
    browserVersion,
    deviceType,
    deviceModel,
  };
}

/**
 * Capture full current client environment telemetry snapshot.
 */
export function getClientEnvironmentSnapshot(): ClientEnvironmentSnapshot {
  const isClient = typeof window !== 'undefined' && typeof navigator !== 'undefined';
  const ua = isClient ? navigator.userAgent : '';
  const parsed = parseUserAgent(ua);

  const isStandalone = isClient
    ? window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      (navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    : false;

  const connection = isClient ? (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection : null;

  return {
    ...parsed,
    screenResolution: isClient ? `${window.screen?.width || 0}x${window.screen?.height || 0}` : 'unknown',
    viewportSize: isClient ? `${window.innerWidth || 0}x${window.innerHeight || 0}` : 'unknown',
    pixelRatio: isClient ? window.devicePixelRatio || 1 : 1,
    isStandalonePWA: isStandalone,
    isOnline: isClient ? navigator.onLine : true,
    connectionType: connection?.effectiveType || connection?.type || undefined,
    connectionRtt: connection?.rtt || undefined,
    language: isClient ? navigator.language : 'ru',
    userAgent: ua,
  };
}
